import React, { useEffect } from 'react';
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLeaf, 
  faFlask, 
  faAward, 
  faUsers,
  faSeedling,
  faTrophy,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { FaCheckCircle } from 'react-icons/fa';
import '../css/carrerhome.css';

const CareerHome = () => {
  useEffect(() => {
    const startRandomCounters = () => {
      const counterElements = document.querySelectorAll('.num');
      
      counterElements.forEach((counter) => {
        const updateCounter = () => {
          const target = parseInt(counter.getAttribute('data-val'));
          const duration = 2000;
          const pauseDuration = 3000;
          let startTime = Date.now();
          
          const animate = () => {
            const currentTime = Date.now();
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const value = Math.floor(easedProgress * target);
            
            counter.textContent = value + (counter.getAttribute('data-suffix') || '');
            
            if (elapsed < duration) {
              requestAnimationFrame(animate);
            } else {
              setTimeout(() => {
                startTime = Date.now();
                requestAnimationFrame(animate);
              }, pauseDuration);
            }
          };
          
          requestAnimationFrame(animate);
        };
        
        updateCounter();
      });
    };

    startRandomCounters();

    return () => {};
  }, []);

  const counterData = [
    { 
      icon: <FontAwesomeIcon icon={faLeaf} />, 
      value: 100, 
      label: "Natural Ingredients",
      suffix: "%",
      color: "#4CAF50"
    },
    { 
      icon: <FontAwesomeIcon icon={faFlask} />, 
      value: 0, 
      label: "Artificial Additives",
      suffix: "%",
      color: "#FF6F00"
    },
    { 
      icon: <FontAwesomeIcon icon={faAward} />, 
      value: 50, 
      label: "Quality Checks",
      suffix: "+",
      color: "#FFD700"
    },
    { 
      icon: <FontAwesomeIcon icon={faUsers} />, 
      value: 10000, 
      label: "Happy Customers",
      suffix: "+",
      color: "#2E7D32"
    }
  ];

  const features = [
    {
      icon: <FontAwesomeIcon icon={faSeedling} />,
      title: "100% Natural",
      description: "Sourced from trusted farms with no artificial additives"
    },
    {
      icon: <FontAwesomeIcon icon={faTrophy} />,
      title: "Premium Quality",
      description: "Manufactured with strict GMP & FSSAI standards"
    },
    {
      icon: <FontAwesomeIcon icon={faGlobe} />,
      title: "Farm to Pack",
      description: "Preserving freshness from source to your doorstep"
    }
  ];

  return (
    <section className="terragold-impact-section">   
      {/* Animated Background Elements */}
      <div className="impact-bg-elements">
        <div className="bg-leaf leaf1"></div>
        <div className="bg-leaf leaf2"></div>
        <div className="bg-leaf leaf3"></div>
        <div className="bg-circle-impact circle-impact1"></div>
        <div className="bg-circle-impact circle-impact2"></div>
      </div>
      
      <div className="container">
        <div className="row align-items-center">
          <div className="col-12 col-lg-6 mb-5 mb-lg-0">
            <motion.div
              className="impact-content"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="badge-impact"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <FaCheckCircle className="badge-icon" /> Why TerraGold
              </motion.div>

              <h1>Pure Nutrition, <br /><span className="highlight-text">Trusted Quality</span></h1>
              <p className="impact-description">
                At TerraGold, we're committed to delivering the purest organic nutraceuticals 
                that support healthier lives. Every product is crafted with care, backed by 
                science, and trusted by thousands.
              </p>
              
              <div className="feature-grid">
                {features.map((feature, index) => (
                  <motion.div 
                    key={index}
                    className="feature-item"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <div className="feature-icon-wrapper">
                      {feature.icon}
                    </div>
                    <div>
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
<Link to="/demo" style={{ textDecoration: 'none' }}>
              <motion.button
                className="impact-cta-button"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(46, 125, 50, 0.3)" }}
                whileTap={{ scale: 0.95 }}
              >
                Discover Our Products
                <span className="btn-arrow">→</span>
              </motion.button>
              </Link>
            </motion.div>
          </div>
          
          {/* Counter Cards */}
          <div className="col-lg-6">
            <div className="counter-grid-wrapper">
              <div className="row g-3">
                {counterData.map((item, index) => (
                  <div className="col-6 col-sm-6" key={index}>
                    <CounterCard item={item} index={index} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CounterCard = ({ item, index }) => (
  <motion.div
    className="impact-counter-box"
    whileHover={{ 
      y: -8,
      boxShadow: "0 15px 35px rgba(46, 125, 50, 0.12)"
    }}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
    viewport={{ once: true }}
  >
    <div className="counter-inner text-center">
      <div className="counter-icon" style={{ color: item.color }}>
        {item.icon}
      </div>
      <h3 className="num" data-val={item.value} data-suffix={item.suffix || ''}>
        0
      </h3>
      <p className="counter-label">{item.label}</p>
    </div>
  </motion.div>
);

export default CareerHome;